"use client";

import { Skeleton } from "@repo/ui";
import confetti from "canvas-confetti";
import {
  Check,
  Clock,
  Copy,
  ListChecks,
  MapPin,
  Share2,
  Users,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useMemo, useRef } from "react";

import { ApplicationRequirements } from "@/app/(platform)/me/opportunities/_components/opportunity-application-requirements";
import {
  CAROUSEL_CONTAINER_CLASS,
  CarouselArrows,
  useDragScroll,
} from "@/components/home/carousel-primitives";
import { MarketingVideo } from "@/components/marketing/marketing-video";
import {
  type AdvertisedPayListing,
  advertisedRateOf,
  AdvertisedRatesProvider,
  isAdvertisedRatePending,
  joinerRateOf,
  PayPlaceholder,
  TemporaryRateNote,
  TemporaryStandingRate,
  useAdvertisedRates,
} from "@/components/opportunities/advertised-pay";
import {
  OpportunityCard,
  type OpportunityListingCard,
} from "@/components/opportunities/opportunity-card";
import { OpportunityListingSections } from "@/components/opportunities/opportunity-listing-sections";
import {
  TRUST_VIDEO_DESCRIPTION,
  TRUST_VIDEO_HEADING,
  TRUST_VIDEO_POSTER,
  TRUST_VIDEO_SRC,
  TRUST_VIDEO_TITLE,
} from "@/config/trust-video";
import { useOpportunityShareLink } from "@/hooks/use-opportunity-share-link";
import { useParticipationStatuses } from "@/hooks/use-participation-statuses";
import { useReferralCap } from "@/hooks/use-referral-cap";
import { useSession } from "@/hooks/use-session";
import {
  APPLICATION_STEPS,
  computeCompletedRequirements,
  getInterviewStepStatus,
  isInterviewStepDone,
} from "@/lib/onboarding-completion";
import {
  formatEngagementBadge,
  formatPayCardDisplay,
  formatReferralCapFor,
  formatWorkMode,
  resolvedPayInput,
  shouldHideFromBrowse,
} from "@/lib/opportunity-utils";
import { api } from "@/lib/trpc/react";
import { shuffle } from "@/lib/utils";

interface OpportunityDetailViewProps {
  token: string;
}

const APPLY_CTA_CLASS =
  "mt-3 flex w-full items-center justify-center gap-2 rounded-[10px] bg-black px-4 py-2.5 text-[13.5px] font-medium text-white no-underline transition-opacity hover:opacity-85";

function ReferralAwareApplyLink({
  recruitmentToken,
  label,
}: {
  recruitmentToken: string;
  label: string;
}) {
  const referralToken = useSearchParams().get("ref");
  const href = `/apply/start/${encodeURIComponent(recruitmentToken)}${
    referralToken ? `?ref=${encodeURIComponent(referralToken)}` : ""
  }`;

  return (
    <Link href={href} className={APPLY_CTA_CLASS}>
      {label}
    </Link>
  );
}

// 3 cards per row on desktop (matches the gap-3 in CAROUSEL_CONTAINER_CLASS).
const EXPLORE_CARD_CLASS =
  "w-[85vw] max-w-[360px] shrink-0 snap-start sm:w-[calc((100%-12px)/2)] sm:max-w-none lg:w-[calc((100%-24px)/3)]";

export function OpportunityDetailView({ token }: OpportunityDetailViewProps) {
  const { user } = useSession();
  const referralCapAmount = useReferralCap();
  const exploreScrollRef = useRef<HTMLDivElement>(null);
  useDragScroll(exploreScrollRef);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [token]);

  const { data: opportunity, isLoading } =
    api.opportunityListing.getByToken.useQuery({ token, allowClosed: true });
  const { data: otherListings } = api.opportunityListing.listPublished.useQuery(
    { limit: 20 },
  );

  const { statusMap } = useParticipationStatuses({
    enabled: Boolean(user),
  });

  // Annotated because `getByToken` infers as `any`, which would otherwise leave
  // the one field this change keys the rate read on unchecked.
  const studyId: string | null | undefined = opportunity?.studyId;
  // Nothing referral-shaped renders, or mints, until the listing says its
  // study can be referred.
  const referralsEnabled: boolean = opportunity?.referralsEnabled === true;

  const submissionStatus = studyId ? (statusMap.get(studyId) ?? null) : null;

  const { data: onboardingData, isLoading: isOnboardingLoading } =
    api.onboarding.getData.useQuery(undefined, { enabled: Boolean(user) });

  const interviewStepStatus = useMemo(
    () => getInterviewStepStatus(submissionStatus),
    [submissionStatus],
  );

  const completedSteps = useMemo(
    () =>
      onboardingData ? computeCompletedRequirements(onboardingData) : null,
    [onboardingData],
  );
  const interviewStepDone = isInterviewStepDone(interviewStepStatus);
  const completedCount =
    (completedSteps?.size ?? 0) + (interviewStepDone ? 1 : 0);
  const allRequiredComplete = completedSteps
    ? APPLICATION_STEPS.filter((s) => s.required).every((s) =>
        completedSteps.has(s.key),
      )
    : false;

  const isOnboardingComplete =
    onboardingData?.user.onboardingFlowStatus === "COMPLETED";

  // Randomized once per pool so the rail doesn't repeat the same listings.
  const otherJobs: OpportunityListingCard[] = useMemo(() => {
    const pool = (otherListings?.items ?? []).filter((j) => {
      if (j.token === token) {
        return false;
      }
      if (!j.studyId) {
        return true;
      }
      return !shouldHideFromBrowse(statusMap.get(j.studyId) ?? undefined);
    });
    return shuffle(pool).slice(0, 12);
  }, [otherListings, token, statusMap]);

  // One read for the page: this listing plus the whole "explore more" pool.
  // Keyed off the pool rather than the twelve it shuffles down to, so the
  // shuffle does not refetch. The page prefetches this listing's own answer on
  // the server, so the figure is in the first paint; the pool widens the key
  // once it loads, and the hook holds that answer meanwhile.
  const rates = useAdvertisedRates(
    useMemo(
      () => [
        studyId,
        ...(otherListings?.items ?? []).map((item) => item.studyId),
      ],
      [studyId, otherListings],
    ),
  );

  const {
    displayLink,
    copied,
    copyLink,
    isLoading: isLoadingReferral,
  } = useOpportunityShareLink({
    token,
    source: "MARKETING_OPPORTUNITY",
    enabled: Boolean(user) && isOnboardingComplete && referralsEnabled,
  });

  const hasConfettied = useRef(false);
  const copyButtonRef = useRef<HTMLButtonElement>(null);

  const handleCopyAndCelebrate = useCallback(() => {
    void copyLink();
    if (!hasConfettied.current && copyButtonRef.current) {
      hasConfettied.current = true;
      const rect = copyButtonRef.current.getBoundingClientRect();
      const x = (rect.left + rect.width / 2) / window.innerWidth;
      const y = rect.top / window.innerHeight;
      void confetti({ particleCount: 80, spread: 60, origin: { x, y } });
    }
  }, [copyLink]);

  const applyUrl = opportunity?.recruitmentToken
    ? `/apply/start/${encodeURIComponent(opportunity.recruitmentToken)}`
    : null;

  if (isLoading) {
    return (
      <div className="animate-[fadeUp_0.3s_ease_both]">
        <div className="mx-auto max-w-[1200px] px-5 pb-15 sm:px-10 sm:pb-20">
          <div className="pt-8 pb-5">
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="pt-2">
            <Skeleton className="mb-3 h-10 w-2/3" />
            <div className="flex gap-2">
              <Skeleton className="h-7 w-28 rounded-md" />
              <Skeleton className="h-7 w-20 rounded-md" />
            </div>
          </div>
          <div className="mt-8 grid grid-cols-1 items-start gap-10 min-[960px]:grid-cols-[1fr_340px]">
            <div>
              <Skeleton className="mb-2 h-4 w-full" />
              <Skeleton className="mb-2 h-4 w-full" />
              <Skeleton className="mb-2 h-4 w-3/4" />
              <Skeleton className="mt-6 mb-2 h-4 w-full" />
              <Skeleton className="mb-2 h-4 w-full" />
              <Skeleton className="mb-2 h-4 w-full" />
            </div>
            <div className="hidden min-[960px]:block">
              <Skeleton className="h-48 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return null;
  }

  const isClosed = Boolean(opportunity.isClosed);

  // The figure and the cadence badge beside it read one object, so a resolved
  // rate quoted per hour can never sit under a badge the listing called
  // one-time.
  // Hoisted because the temporary-rate note under the figure formats the
  // standing rate it displaced from the same listing.
  const payListing: AdvertisedPayListing = {
    payRateFrequency: opportunity.payRateFrequency,
    estimatedDurationMins: opportunity.estimatedDurationMins,
    category: opportunity.category,
  };
  const payPending = isAdvertisedRatePending(rates, studyId);
  const payInput = resolvedPayInput({
    rate: advertisedRateOf(rates, studyId),
    listing: payListing,
  });
  const payCard = formatPayCardDisplay(payInput);
  const payDisplay = payCard?.amount ?? "";
  const paySuffix = payCard?.label ?? "";
  const engagementBadge = formatEngagementBadge(payInput);
  // Per-task reads as deliverables; hourly and one-time are both time-bound.
  const EngagementIcon = engagementBadge === "Per task" ? ListChecks : Clock;

  const returnUrl = `/me/opportunities?listing=${token}`;
  const onboardingReturnUrl = `/join?returnUrl=${encodeURIComponent(returnUrl)}`;

  const referralCapDisplay = payPending
    ? null
    : formatReferralCapFor(joinerRateOf(rates, studyId), referralCapAmount);

  const ctaLabel = !user
    ? "Apply to this opportunity"
    : isOnboardingComplete
      ? "Start application"
      : "Continue application";

  const applyCard = (
    <div className="border-border bg-card rounded-2xl border p-5">
      <div className="mb-4.5">
        {payPending && <PayPlaceholder className="h-6 w-28" />}
        {payDisplay && (
          <>
            <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
              <span className="text-muted-foreground text-[13px]">Earn</span>
              <TemporaryStandingRate
                rate={advertisedRateOf(rates, studyId)}
                listing={payListing}
                className="text-muted-foreground/50 text-[20px] font-normal"
              />
              <span className="text-foreground text-[24px] leading-none font-semibold tracking-[-0.03em]">
                {payDisplay}
              </span>
              {paySuffix && (
                <span className="text-muted-foreground text-[13px]">
                  {paySuffix}
                </span>
              )}
            </div>
            <TemporaryRateNote
              rate={advertisedRateOf(rates, studyId)}
              className="text-muted-foreground/75 mt-[3px] block text-[13px]"
            />
          </>
        )}
      </div>

      <ApplicationRequirements
        completedSteps={completedSteps}
        isLoading={user ? isOnboardingLoading : false}
        allComplete={allRequiredComplete}
        completedCount={completedCount}
        variant="card"
        interviewStatus={interviewStepStatus}
        opportunityTitle={opportunity.title ?? undefined}
        onboardingUrl={
          user && !isOnboardingComplete ? onboardingReturnUrl : undefined
        }
      />

      {submissionStatus ? null : applyUrl && opportunity?.recruitmentToken ? (
        <Suspense
          fallback={
            <Link href={applyUrl} className={APPLY_CTA_CLASS}>
              {ctaLabel}
            </Link>
          }
        >
          <ReferralAwareApplyLink
            recruitmentToken={opportunity.recruitmentToken}
            label={ctaLabel}
          />
        </Suspense>
      ) : (
        <div className="border-border bg-background text-foreground mt-3 flex w-full items-center justify-center gap-2 rounded-[10px] border px-4 py-2.5 text-[13.5px] font-medium">
          Applications opening soon
        </div>
      )}
      {!submissionStatus && (
        <p className="text-muted-foreground/60 mt-2 text-center text-[11.5px] font-light">
          You&apos;ll start with a short screening interview
        </p>
      )}
    </div>
  );

  const closedCard = (
    <div className="border-border bg-card rounded-2xl border p-5">
      <div className="text-foreground mb-2 text-[16px] font-semibold tracking-[-0.01em]">
        Find similar paid work
      </div>
      <p className="text-muted-foreground mb-5 text-[13px] leading-[1.55] font-light">
        {user
          ? "This listing has closed, but new paid opportunities open regularly. Browse open opportunities to find similar work."
          : "This listing has closed, but Terac posts new paid opportunities regularly. Sign up to get matched with similar work."}
      </p>
      <Link
        href="/opportunities"
        className="flex w-full items-center justify-center gap-2 rounded-[10px] bg-black px-4 py-2.5 text-[13.5px] font-medium text-white no-underline transition-opacity hover:opacity-85"
      >
        Browse open opportunities
      </Link>
      {!user && (
        <Link
          href="/login"
          className="border-border text-foreground hover:bg-muted mt-2 flex w-full items-center justify-center gap-2 rounded-[10px] border bg-transparent px-4 py-2.5 text-[13.5px] font-medium no-underline transition-colors"
        >
          Sign up to Terac
        </Link>
      )}
    </div>
  );

  return (
    <div className="animate-[fadeUp_0.3s_ease_both]">
      <div className="mx-auto max-w-[1200px] px-5 pb-15 sm:px-10 sm:pb-20">
        {/* Back button */}
        <div className="pt-8 pb-5">
          <Link
            href="/opportunities"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-[13px] no-underline transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            All opportunities
          </Link>
        </div>

        {/* Hero */}
        <div className="pt-2">
          <h1 className="mb-3 text-[26px] leading-[1.15] font-semibold tracking-[-0.03em] text-black sm:text-[36px] dark:text-white">
            {opportunity.title}
          </h1>
          <div className="flex items-center gap-2">
            {engagementBadge && (
              <span className="border-border text-muted-foreground inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[13px] font-medium">
                <EngagementIcon size={14} />
                {engagementBadge}
              </span>
            )}
            <span className="border-border text-muted-foreground inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[13px] font-medium">
              <MapPin size={14} />
              {formatWorkMode(opportunity.workMode)}
            </span>
          </div>
        </div>

        {/* Mobile apply card */}
        <div className="mt-6 block min-[960px]:hidden">
          {isClosed ? closedCard : applyCard}
        </div>

        {/* Two-column layout */}
        <div className="mt-8 grid grid-cols-1 items-start gap-10 min-[960px]:grid-cols-[1fr_340px]">
          {/* Main content */}
          <div>
            <OpportunityListingSections
              scale="detail"
              descriptionMarkdown={opportunity.descriptionMarkdown}
              responsibilities={opportunity.responsibilities}
              requirements={opportunity.requirements}
              payRateFrequency={opportunity.payRateFrequency}
            />

            {/* Posted by */}
            <div className="border-border flex items-center gap-3 border-y py-2.5">
              <Image src="/brand/logo.svg" alt="Terac" width={28} height={28} />
              <div className="leading-tight">
                <div className="text-foreground text-[14px] font-medium">
                  Posted by Terac
                </div>
                <span className="text-muted-foreground text-[12.5px]">
                  terac.com
                </span>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="sticky top-[78px] hidden flex-col gap-3 min-[960px]:flex">
            {isClosed ? (
              closedCard
            ) : (
              <>
                {applyCard}

                {referralsEnabled && (
                  <div className="border-border bg-card rounded-2xl border p-7">
                    <div className="text-foreground mb-1.5 text-[16px] font-semibold tracking-[-0.01em]">
                      {referralCapDisplay ? (
                        <>
                          Earn up to{" "}
                          <span className="bg-yellow-400 px-1 text-black">
                            {referralCapDisplay}
                          </span>{" "}
                          per person you refer
                        </>
                      ) : (
                        "Share this opportunity"
                      )}
                    </div>
                    <p className="text-muted-foreground mb-5 text-[13px] leading-[1.5] font-light">
                      {referralCapDisplay
                        ? `Know someone who'd be a great fit? Share your link and earn a share of everything they are paid on Terac, up to ${referralCapDisplay} per person.`
                        : "Know someone who would be a great fit? Share the link and help them discover this opportunity."}
                    </p>
                    {user && isOnboardingLoading ? (
                      <div className="flex flex-col gap-3">
                        <Skeleton className="h-10 w-full rounded-lg" />
                        <Skeleton className="h-10 w-full rounded-[10px]" />
                      </div>
                    ) : user && isOnboardingComplete ? (
                      <>
                        <div className="border-border bg-background mb-3 flex h-10 items-center overflow-hidden rounded-lg border pr-1 pl-3">
                          {isLoadingReferral ? (
                            <Skeleton className="h-4 w-3/4" />
                          ) : (
                            <span className="flex-1 truncate text-[13px] text-blue-500/80 dark:text-blue-400">
                              {displayLink}
                            </span>
                          )}
                          <button
                            className="text-muted-foreground hover:bg-muted hover:text-foreground ml-auto flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent transition-colors disabled:cursor-default disabled:opacity-50"
                            onClick={copyLink}
                            disabled={isLoadingReferral}
                          >
                            {copied ? (
                              <Check
                                size={14}
                                className="text-[#3ECF8E]"
                                strokeWidth={2.5}
                              />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                        </div>
                        <button
                          ref={copyButtonRef}
                          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] bg-black px-4 py-2.5 text-[13px] font-medium text-white transition-opacity hover:opacity-85 disabled:cursor-default disabled:opacity-50"
                          onClick={handleCopyAndCelebrate}
                          disabled={isLoadingReferral}
                        >
                          {copied ? "Copied!" : "Copy Link & Earn"}
                          <Share2 size={14} />
                        </button>
                      </>
                    ) : user ? (
                      <Link
                        href={onboardingReturnUrl}
                        className="border-border text-foreground hover:bg-muted flex w-full items-center justify-center gap-2 rounded-[10px] border bg-transparent px-4 py-2.5 text-[13.5px] font-medium no-underline transition-colors"
                      >
                        Complete Onboarding
                      </Link>
                    ) : (
                      <Link
                        href={`/login?returnUrl=${encodeURIComponent(onboardingReturnUrl)}`}
                        className="flex w-full items-center justify-center gap-2 rounded-[10px] bg-black px-4 py-2.5 text-[13.5px] font-medium text-white no-underline transition-opacity hover:opacity-85"
                      >
                        Login & Share
                      </Link>
                    )}
                    <div className="text-muted-foreground/40 mt-3.5 flex items-center justify-center gap-1.5 text-[11px]">
                      <Users size={12} />
                      1,200+ people have earned through referrals
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* About Terac */}
        <div className="border-border mt-8 border-t pt-7">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
            <h2 className="text-foreground shrink-0 text-[18px]! leading-[1.2]! font-semibold! tracking-[-0.01em]! sm:text-[20px]!">
              {TRUST_VIDEO_HEADING}
            </h2>
            <p className="text-muted-foreground/70 text-sm leading-[1.6] font-light">
              {TRUST_VIDEO_DESCRIPTION}
            </p>
          </div>
          <div className="grid max-w-[960px] items-stretch gap-4 sm:grid-cols-2">
            <div className="border-border bg-card overflow-hidden rounded-xl border">
              <div className="relative aspect-video w-full">
                <MarketingVideo
                  src={TRUST_VIDEO_SRC}
                  poster={TRUST_VIDEO_POSTER}
                  title={TRUST_VIDEO_TITLE}
                  preload="none"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            </div>
            <Link
              href="/about"
              className="group border-border bg-card hover:border-foreground/15 flex min-w-0 flex-col rounded-xl border p-5 no-underline transition-colors"
            >
              <div className="text-foreground text-[15px] font-semibold tracking-[-0.01em]">
                The expert network for AI training and research
              </div>
              <p className="text-muted-foreground mt-1.5 text-[13px] leading-[1.55] font-light">
                We connect professionals like you with leading companies for
                paid projects that fit your expertise.
              </p>
              <span className="text-foreground group-hover:text-brand-blue mt-auto inline-flex items-center gap-1 pt-4 text-[13px] font-medium transition-colors">
                Learn more about Terac
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-transform duration-200 group-hover:translate-x-0.5"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </Link>
          </div>
        </div>

        {/* Explore more */}
        {otherJobs.length > 0 && (
          <div className="border-border mt-8 border-t pt-7 pb-4">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-foreground text-[18px] font-semibold tracking-[-0.02em]">
                Explore more opportunities
              </div>
              <CarouselArrows scrollRef={exploreScrollRef} />
            </div>
            <div ref={exploreScrollRef} className={CAROUSEL_CONTAINER_CLASS}>
              <AdvertisedRatesProvider rates={rates}>
                {otherJobs.map((oj) => (
                  <div key={oj.id} className={EXPLORE_CARD_CLASS}>
                    <OpportunityCard
                      opportunity={oj}
                      submissionStatus={
                        oj.studyId ? (statusMap.get(oj.studyId) ?? null) : null
                      }
                    />
                  </div>
                ))}
              </AdvertisedRatesProvider>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
